/**
 * FeatureDetect
 * Util functions that help to detect browser features (i.e.
 * arrayFrom, Promise, URL, Fetch, etc)
 */
/**
 * Generic public functions
 */
/**
 * has
 * Helper to determine whether all of the provided features
 * are detected.
 * @param checks i.e. has([hasArrayPrototypeFind, hasArrayFrom, etc]);
 */
export var has = function (checks) {
    for (var i = 0; i < checks.length; i++) {
        if (!checks[i]()) {
            return false;
        }
    }
    return true;
};
export var getDetectorName = function (detector) {
    return detector && detector.hasOwnProperty("_name") ? detector._name : "not defined";
};
/**
 * BrowserFeature utils
 */
export var hasArrayPrototypeFind = function () { return "find" in Array.prototype; };
hasArrayPrototypeFind._name = "hasArrayPrototypeFind";
export var hasArrayPrototypeSome = function () { return "some" in Array.prototype; };
hasArrayPrototypeSome._name = "hasArrayPrototypeSome";
export var hasArrayPrototypeIncludes = function () { return "includes" in Array.prototype; };
hasArrayPrototypeIncludes._name = "hasArrayPrototypeIncludes";
export var hasArrayFrom = function () {
    var isValid = function () {
        try {
            /**
             * @note - disabled because some host website have already polyfilled this
             * feature, even though the browser already supports it, but are not compatible with
             * this check.
             * Disabling it is something we had to do to make it less strict
             */
            // Array.from({ length: -Infinity });
            return true;
        }
        catch (e) {
            return false;
        }
    };
    return "from" in Array && isValid();
};
hasArrayFrom._name = "hasArrayFrom";
export var hasElementPrototypeRemove = function () { return "Element" in window && "remove" in Element.prototype; };
hasElementPrototypeRemove._name = "hasElementPrototypeRemove";
export var hasPromise = function () { return "Promise" in window; };
hasPromise._name = "hasPromise";
export var hasIsInteger = function () { return "isInteger" in Number; };
hasIsInteger._name = "hasIsInteger";
export var hasObjectAssign = function () { return "assign" in Object; };
hasObjectAssign._name = "hasObjectAssign";
export var hasUrl = function () {
    /*
     * Browsers may have:
     * No global URL object
     * URL with static methods only - may have a dummy constructor
     * URL with members except searchParams
     * Full URL API support
     */
    try {
        var nativeURL = new window.URL("http://example.com");
        if ("href" in nativeURL && "searchParams" in nativeURL) {
            var url = new URL("http://example.com");
            url.search = "a=1&b=2";
            if (url.href === "http://example.com/?a=1&b=2") {
                url.search = "";
                if (url.href === "http://example.com/") {
                    var sp1 = new window.URLSearchParams("a=1");
                    // IE Edge 18 will break on the line below because of invalid argument.
                    // therefor the workaround below that line. I'am not sure about
                    // the implications yet...
                    // const sp2 = new window.URLSearchParams(sp1);
                    var sp2 = new window.URLSearchParams(String(sp1));
                    if (String(sp2) === "a=1") {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    catch (error) {
        return false;
    }
};
hasUrl._name = "hasUrl";
export var hasFetch = function () { return "fetch" in window; };
hasFetch._name = "hasFetch";
export var hasSet = function () { return "Set" in window && isSetOrMapValid("Set"); };
hasSet._name = "hasSet";
export var hasMap = function () { return "Map" in window && isSetOrMapValid("Map"); };
hasMap._name = "hasMap";
export var hasServiceWorker = function () { return "serviceWorker" in navigator; };
hasServiceWorker._name = "hasServiceWorker";
export var hasLocalStorage = function () { return hasBrowserStorage(StorageType.Local); };
hasLocalStorage._name = "hasLocalStorage";
export var hasSessionStorage = function () { return hasBrowserStorage(StorageType.Session); };
hasSessionStorage._name = "hasSessionStorage";
export var hasRequestAnimationFrame = function () { return "requestAnimationFrame" in window; };
hasRequestAnimationFrame._name = "hasRequestAnimationFrame";
export var hasSymbol = function () { return "Symbol" in window && window.Symbol.length === 0; };
hasSymbol._name = "hasSymbol";
export var hasIntl = function () { return "Intl" in window; };
hasSymbol._name = "hasIntl";
export var hasMutationObserver = function () { return "MutationObserver" in self; };
hasMutationObserver._name = "hasMutationObserver";
export var hasStringRepeat = function () { return "repeat" in String.prototype; };
hasStringRepeat._name = "hasStringRepeat";
/**
 * Detection private helpers
 */
var StorageType;
(function (StorageType) {
    StorageType["Local"] = "localStorage";
    StorageType["Session"] = "sessionStorage";
})(StorageType || (StorageType = {}));
/**
 * @Detector
 */
var hasBrowserStorage = function (type) {
    var storage;
    try {
        storage = window[type];
        var x = "__storage_test__";
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    }
    catch (e) {
        return (e instanceof DOMException &&
            // everything except Firefox
            (e.code === 22 ||
                // Firefox
                e.code === 1014 ||
                // test name field too, because code might not be present
                // everything except Firefox
                e.name === "QuotaExceededError" ||
                // Firefox
                e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
            // acknowledge QuotaExceededError only if there's something already stored
            storage.length !== 0);
    }
};
/**
 * Helper
 */
var isSetOrMapValid = function (type) {
    try {
        var sorm = type === "Set" ? new Set([1, 2]) : new Map([[1, 1], [2, 2]]); // sorm = set or map
        if (Set.length === 0) {
            if (sorm.size === 2) {
                if ("Symbol" in window && "iterator" in Symbol && typeof sorm[Symbol.iterator] === "function") {
                    return true;
                }
            }
        }
        return false;
    }
    catch (e) {
        return false;
    }
};
//# sourceMappingURL=FeatureDetect.js.map